import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Building2,
  Grid,
  Settings,
  LogOut,
  LayoutDashboard,
  Package,
  Users,
  BarChart3,
  Shield,
  FileText,
  ChevronDown,
  ChevronRight,
  Truck,
  DollarSign,
  Calculator,
  Landmark,
  UserCog,
  Tag,
  BookOpen,
  ClipboardList,
  Wallet,
  Receipt,
  Wrench,
  Percent,
  Clock,
  UtensilsCrossed,
  ShieldCheck,
  CalendarDays,
  Printer,
  Building,
  CreditCard,
  ShoppingCart,
  Star,
  Coins,
  TrendingUp,
  Banknote,
  Globe,
  MessageCircle,
  Lock,
  Car,
  Bed,
  Stethoscope,
  Pill,
  Smile,
  Glasses,
  HeartPulse,
  HardHat,
  Sofa,
  Scissors,
  Dumbbell,
  Droplets,
  Smartphone,
  Laptop,
  Shirt,
  Footprints,
  Gem,
  Sparkles,
  Wine,
  Croissant,
  Store,
  Calendar,
  Fuel,
  Menu,
  X,
} from "lucide-react";
import { useTenant } from "../../lib/TenantContext";
import { useBusinessConfig } from "../../hooks/useBusinessConfig";
import AiChat from "../ai/AiChat";
import OfflineBanner from "../OfflineBanner";

// Icon map — maps icon names to components
const ICON_MAP: Record<
  string,
  React.FC<{ size?: number; strokeWidth?: number }>
> = {
  DollarSign,
  Users,
  Package,
  Wallet,
  BookOpen,
  Calculator,
  Clock,
  ShieldCheck,
  Building,
  FileText,
  TrendingUp,
  Globe,
  Shield,
  Settings,
  UserCog,
  BarChart3,
  ClipboardList,
  Tag,
  Grid,
  Truck,
  ShoppingCart,
  CreditCard,
  Star,
  Percent,
  Printer,
  Receipt,
  Coins,
  Banknote,
  Landmark,
  Lock,
  MessageCircle,
  CalendarDays,
  UtensilsCrossed,
  Wrench,
  Calendar,
  Car,
  Bed,
  Stethoscope,
  Pill,
  Smile,
  Glasses,
  HeartPulse,
  HardHat,
  Sofa,
  Scissors,
  Dumbbell,
  Droplets,
  Smartphone,
  Laptop,
  Shirt,
  Footprints,
  Gem,
  Sparkles,
  Wine,
  Croissant,
  Store,
  Fuel,
  LayoutDashboard,
  Building2,
};

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  tenant_admin: "Administrador",
  manager: "Gerente",
  accountant: "Contable",
  hr: "Recursos Humanos",
  sales: "Ventas",
  technician: "Técnico",
  supervisor: "Supervisor(a)",
  cashier: "Cajero(a)",
};

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-500 hover:text-gray-800 hover:bg-brand-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300"
      >
        {icon}
        <span className="flex-1 text-left uppercase tracking-wider">
          {title}
        </span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && (
        <div className="ml-2 flex flex-col gap-0.5 mt-0.5">{children}</div>
      )}
    </div>
  );
};

const NavItem: React.FC<{
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: string;
  onNavigate?: () => void;
}> = ({ to, icon, label, badge, onNavigate }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
    end={to === "/"}
    onClick={onNavigate}
  >
    {icon}
    <span className="flex-1">{label}</span>
    {badge && (
      <span className="text-[9px] font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
        {badge}
      </span>
    )}
  </NavLink>
);

const getIcon = (iconName: string, size = 16) => {
  const Icon = ICON_MAP[iconName] || Package;
  return <Icon size={size} strokeWidth={1.5} />;
};

export const Shell: React.FC = () => {
  const { signOut, canAccess, userRole, tenantName } = useTenant();
  const {
    config,
    labels: _labels,
    businessTypeCode: _businessTypeCode,
  } = useBusinessConfig();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <>
      <div className="app-container">
        {/* Desktop Sidebar */}
        <aside className="sidebar hidden lg:flex">
          <div className="p-4 border-b border-subtle flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-primary border border-subtle flex items-center justify-center shadow-sm">
              <Building2 size={18} className="text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-sm font-semibold">
                {tenantName || "OsirisBrain ERP"}
              </h1>
              <p className="text-xs text-tertiary">{config.name}</p>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0 px-2">
            {/* Dashboard — visible only if allowed */}
            {canAccess("/") && (
              <NavItem
                to="/"
                icon={<LayoutDashboard size={16} strokeWidth={1.5} />}
                label="Panel de Control"
              />
            )}

            {/* Dynamic sections from business config */}
            {config.sidebar.map((section) => {
              const visibleItems = section.items.filter((item) => canAccess(item.path));
              if (visibleItems.length === 0) return null;
              return (
                <Section
                  key={section.key}
                  title={section.title}
                  icon={getIcon(section.icon, 12)}
                  defaultOpen={section.defaultOpen}
                >
                  {visibleItems.map((item) => (
                    <NavItem
                      key={item.path}
                      to={item.path}
                      icon={getIcon(item.icon)}
                      label={item.label}
                      badge={item.badge}
                    />
                  ))}
                </Section>
              );
            })}

            {/* Auditoría — always for admins */}
            {canAccess("/hub/audit-log") && (
              <Section title="Seguridad" icon={<Shield size={12} />}>
                <NavItem
                  to="/hub/audit-log"
                  icon={<Shield size={16} strokeWidth={1.5} />}
                  label="Auditoría"
                />
              </Section>
            )}

            {/* DGII — admin / accountant */}
            {(canAccess("/hub/dgii-config") || canAccess("/hub/taxes")) && (
              <Section title="DGII / e-CF" icon={<FileText size={12} />}>
                {canAccess("/hub/dgii-config") && (
                  <NavItem
                    to="/hub/dgii-config"
                    icon={<Shield size={16} strokeWidth={1.5} />}
                    label="Configuración DGII"
                  />
                )}
                {canAccess("/hub/taxes") && (
                  <NavItem
                    to="/hub/taxes"
                    icon={<Percent size={16} strokeWidth={1.5} />}
                    label="Impuestos y Reportes"
                  />
                )}
              </Section>
            )}

            {/* Equipo — admin only */}
            {canAccess("/hub/settings") && (
              <Section title="Equipo" icon={<UserCog size={12} />}>
                <NavItem
                  to="/hub/settings"
                  icon={<Settings size={16} strokeWidth={1.5} />}
                  label="Usuarios y Config"
                />
              </Section>
            )}
          </nav>

          <div className="p-3 border-t border-subtle text-xs text-tertiary flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Shield size={11} /> Rol
              </span>
              <span className="font-bold text-[9px] uppercase bg-brand-100 px-2 py-0.5 rounded text-brand-500">
                {(userRole && ROLE_LABELS[userRole]) || "Sin acceso"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Negocio</span>
              <span className="font-bold text-[9px] uppercase bg-brand-100 px-2 py-0.5 rounded text-brand-500">
                {config.name}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Status</span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-primary border border-subtle"></span>{" "}
                Online
              </span>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="main-content">
          <header className="topbar">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                aria-label="Abrir menú de navegación"
              >
                <Menu size={20} />
              </button>
              <div className="text-lg font-semibold text-primary">
                {tenantName || "Workspace"}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm font-medium text-secondary hidden sm:block">
                OsirisBrain{" "}
                <span className="text-tertiary text-xs ml-1">
                  ({config.name})
                </span>
              </div>
              <OfflineBanner />
              <button
                onClick={signOut}
                className="btn btn-secondary btn-icon text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                title="Log Out / Switch Tenant"
                aria-label="Cerrar sesión"
              >
                <LogOut size={16} />
              </button>
              {canAccess("/hub/settings") && (
                <NavLink
                  to="/hub/settings"
                  className="btn btn-secondary btn-icon"
                  aria-label="Configuración"
                >
                  <Settings size={16} />
                </NavLink>
              )}
            </div>
          </header>

          <div className="content-area">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navegación móvil"
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMobileNav}
          />
          <div className="absolute left-0 top-0 h-full w-[280px] bg-white shadow-2xl flex flex-col">
            <div className="p-4 border-b border-subtle flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-primary border border-subtle flex items-center justify-center shadow-sm">
                  <Building2
                    size={18}
                    className="text-primary"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <h1 className="text-sm font-semibold">
                    {tenantName || "OsirisBrain ERP"}
                  </h1>
                  <p className="text-xs text-tertiary">{config.name}</p>
                </div>
              </div>
              <button
                onClick={closeMobileNav}
                className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300"
                aria-label="Cerrar menú"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0 px-2">
              {canAccess("/") && (
                <NavItem
                  to="/"
                  icon={<LayoutDashboard size={16} strokeWidth={1.5} />}
                  label="Panel de Control"
                  onNavigate={closeMobileNav}
                />
              )}

              {config.sidebar.map((section) => {
                const visibleItems = section.items.filter((item) => canAccess(item.path));
                if (visibleItems.length === 0) return null;
                return (
                  <Section
                    key={section.key}
                    title={section.title}
                    icon={getIcon(section.icon, 12)}
                    defaultOpen={section.defaultOpen}
                  >
                    {visibleItems.map((item) => (
                      <NavItem
                        key={item.path}
                        to={item.path}
                        icon={getIcon(item.icon)}
                        label={item.label}
                        badge={item.badge}
                        onNavigate={closeMobileNav}
                      />
                    ))}
                  </Section>
                );
              })}

              {canAccess("/hub/audit-log") && (
                <Section title="Seguridad" icon={<Shield size={12} />}>
                  <NavItem
                    to="/hub/audit-log"
                    icon={<Shield size={16} strokeWidth={1.5} />}
                    label="Auditoría"
                    onNavigate={closeMobileNav}
                  />
                </Section>
              )}

              {/* DGII — admin / accountant */}
              {(canAccess("/hub/dgii-config") || canAccess("/hub/taxes")) && (
                <Section title="DGII / e-CF" icon={<FileText size={12} />}>
                  {canAccess("/hub/dgii-config") && (
                    <NavItem
                      to="/hub/dgii-config"
                      icon={<Shield size={16} strokeWidth={1.5} />}
                      label="Configuración DGII"
                      onNavigate={closeMobileNav}
                    />
                  )}
                  {canAccess("/hub/taxes") && (
                    <NavItem
                      to="/hub/taxes"
                      icon={<Percent size={16} strokeWidth={1.5} />}
                      label="Impuestos y Reportes"
                      onNavigate={closeMobileNav}
                    />
                  )}
                </Section>
              )}

              {canAccess("/hub/settings") && (
                <Section title="Equipo" icon={<UserCog size={12} />}>
                  <NavItem
                    to="/hub/settings"
                    icon={<Settings size={16} strokeWidth={1.5} />}
                    label="Usuarios y Config"
                    onNavigate={closeMobileNav}
                  />
                </Section>
              )}
            </nav>

            <div className="p-3 border-t border-subtle text-xs text-tertiary flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Shield size={11} /> Rol
                </span>
                <span className="font-bold text-[9px] uppercase bg-brand-100 px-2 py-0.5 rounded text-brand-500">
                  {(userRole && ROLE_LABELS[userRole]) || "Sin acceso"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Negocio</span>
                <span className="font-bold text-[9px] uppercase bg-brand-100 px-2 py-0.5 rounded text-brand-500">
                  {config.name}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Status</span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-primary border border-subtle"></span>{" "}
                  Online
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global AI Chat Widget */}
      <AiChat />
    </>
  );
};
